import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, ChevronRight } from 'lucide-react';
import { useLab, isLabDemoMode, DEMO_LOGIN_CHIPS } from '../LabContext';
import { Card, NoIndex, NimnyArt } from '../LabUI';

// Where to send the user after a successful login. Legacy "max"/"anita"
// children keep their existing /lab/max and /lab/anita routes; new
// child accounts (with their own studentId) land on the dynamic
// /lab/student/<studentId> route.
const homeFor = (user) => {
  if (!user) return '/lab/login';
  if (user.role === 'parent' || user.role === 'super_admin') return '/lab/parent';
  if (user.studentId === 'max') return '/lab/max';
  if (user.studentId === 'anita') return '/lab/anita';
  if (user.studentId) return `/lab/student/${user.studentId}`;
  return '/lab/me';
};

// If the user reached the login page via a redirect from a protected route,
// we stash the original path in location.state.from. Honour it after a
// successful auth as long as it stays inside /lab and the user has access.
const safeFromPath = (loc) => {
  const from = loc?.state?.from;
  if (typeof from !== 'string') return null;
  if (!from.startsWith('/lab/')) return null;
  if (from.startsWith('/lab/login')) return null;
  return from;
};

const targetAfterAuth = (user, loc) => {
  const from = safeFromPath(loc);
  if (!from) return homeFor(user);
  // Children may only return to their own subtree.
  if (user && user.role === 'child') {
    const sid = user.studentId;
    const ownPrefix = sid === 'max' || sid === 'anita'
      ? `/lab/${sid}`
      : `/lab/student/${sid}`;
    if (from === ownPrefix || from.startsWith(`${ownPrefix}/`)) return from;
    return homeFor(user);
  }
  return from;
};

const LabLogin = () => {
  const { user, login, language } = useLab();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const demoMode = isLabDemoMode();

  // If already logged in, jump to the right home (or the original target).
  useEffect(() => {
    if (user) navigate(targetAfterAuth(user, location), { replace: true });
  }, [user, navigate, location]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!loginId.trim() || !password) {
      setError(
        language === 'ru'
          ? 'Введите логин и пароль.'
          : 'Enter your login and password.'
      );
      return;
    }
    setBusy(true);
    try {
      const u = await login(loginId.trim(), password);
      navigate(targetAfterAuth(u, location), { replace: true });
    } catch (err) {
      setError(
        (err && err.message && err.message !== 'Login failed' && err.message)
          ||
          (language === 'ru'
            ? 'Неверный логин или пароль.'
            : 'Wrong login or password.')
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative text-[#f5ebe0]">
      <NoIndex />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#0a0f24] via-[#10183a] to-[#0a0f24]"
        aria-hidden
      />

      <div className="relative max-w-md mx-auto px-5 pt-8 pb-12">
        <div className="flex flex-col items-center text-center">
          <NimnyArt size="md" />
          <h1
            className="mt-3 text-3xl text-[#f3d9a4]"
            style={{ fontFamily: 'var(--font-heading)' }}
            data-testid="lab-login-title"
          >
            {language === 'ru' ? 'Вход в Nimny Lab' : 'Sign in to Nimny Lab'}
          </h1>
          <p
            className="mt-2 text-amber-200/70 text-sm font-sans"
            data-testid="lab-login-subtitle"
          >
            {language === 'ru'
              ? 'Введите ваш логин и пароль.'
              : 'Enter your login and password.'}
          </p>
        </div>

        <Card className="mt-6 p-5">
          <form onSubmit={onSubmit} className="space-y-4" data-testid="lab-login-form">
            {/* Login ID */}
            <div>
              <label
                htmlFor="lab-login-id"
                className="text-xs uppercase tracking-wider text-amber-200/60 font-sans mb-2 block"
              >
                {language === 'ru' ? 'Логин' : 'Login ID'}
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-amber-200/50 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="lab-login-id"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={loginId}
                  onChange={(e) => {
                    setLoginId(e.target.value);
                    setError('');
                  }}
                  placeholder={language === 'ru' ? 'например, parent' : 'e.g., parent'}
                  data-testid="lab-login-loginid"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0c1230]/60 border border-amber-300/15 text-[#f5ebe0] placeholder:text-amber-200/30 focus:outline-none focus:border-amber-300/40 font-sans text-base"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="lab-login-password"
                className="text-xs uppercase tracking-wider text-amber-200/60 font-sans mb-2 block"
              >
                {language === 'ru' ? 'Пароль' : 'Password'}
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-amber-200/50 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  id="lab-login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder={language === 'ru' ? 'Введите пароль' : 'Enter password'}
                  data-testid="lab-login-password"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0c1230]/60 border border-amber-300/15 text-[#f5ebe0] placeholder:text-amber-200/30 focus:outline-none focus:border-amber-300/40 font-sans text-base"
                />
              </div>
            </div>

            {error && (
              <div
                role="alert"
                data-testid="lab-login-error"
                className="text-red-300/90 text-sm font-sans"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !loginId.trim() || !password}
              data-testid="lab-login-submit"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#d4a574] hover:bg-[#c89860] disabled:opacity-40 disabled:cursor-not-allowed text-[#1a1208] font-sans transition-colors"
            >
              {busy
                ? language === 'ru'
                  ? 'Входим…'
                  : 'Signing in…'
                : language === 'ru'
                  ? 'Войти'
                  : 'Sign in'}
              {!busy && <ChevronRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Demo-mode quick-login chips. Hidden in production. */}
          {demoMode && (
            <div
              className="mt-5 pt-4 border-t border-amber-300/10"
              data-testid="lab-login-demo-panel"
            >
              <div className="text-[10px] uppercase tracking-wider text-amber-200/55 font-sans mb-2">
                {language === 'ru' ? 'Demo: быстрый вход' : 'Demo: quick fill'}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {DEMO_LOGIN_CHIPS.map((a) => (
                  <button
                    key={a.login}
                    type="button"
                    onClick={() => {
                      setLoginId(a.login);
                      setPassword('');
                      setError('');
                    }}
                    data-testid={`lab-login-demo-${a.login}`}
                    className="py-2 rounded-xl border border-amber-300/15 text-amber-200/75 text-xs font-sans hover:bg-amber-300/5"
                  >
                    {language === 'ru' ? a.labelRu : a.labelEn}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>

        <div className="mt-6 text-center text-xs text-amber-200/50 font-sans">
          {language === 'ru'
            ? 'Это закрытое пространство вашей семьи.'
            : "This is your family's private space."}
        </div>

        <p
          className="mt-6 text-center text-amber-200/30 font-sans text-xs"
          data-testid="lab-copyright"
        >
          © 2026 Nimny LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LabLogin;
